import { ChangeEvent, FC } from "react";
import { useTranslation } from "react-i18next";

import { formatDurationSecondsCompact, NO_VALUE_DISPLAY } from "@ryde/shared";

import {
  IStationTimingLegOption,
  StationTimingOptionKindEnum,
} from "./station-timing-preview.models";
import {
  buildStationTimingOptionLabel,
  formatTrafficSlotHoursRange,
} from "./station-timing-preview.utils";

interface IStationTimingPreviewOptionProps {
  option: IStationTimingLegOption;
  /** "A -> B" text of the leg, used to keep the radio's accessible name self-contained. */
  legName: string;
  radioGroupName: string;
  isSelected: boolean;
  onOptionChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

export const StationTimingPreviewOption: FC<
  IStationTimingPreviewOptionProps
> = ({ option, legName, radioGroupName, isSelected, onOptionChange }) => {
  const { t } = useTranslation();
  const optionLabel = buildStationTimingOptionLabel({ option, t });
  const timeLabel =
    formatDurationSecondsCompact(option.seconds) || NO_VALUE_DISPLAY;
  const hoursRange = formatTrafficSlotHoursRange(option.hours);
  // The way back out of a leg reads as an escape hatch, not as a fourth measurement to consider.
  const chipModifier =
    option.kind === StationTimingOptionKindEnum.Planned
      ? " station-timing__chip--planned"
      : "";

  return (
    // A real radio, only visually replaced: the native control keeps arrow-key navigation inside the
    // leg's group, and the chip body next to it carries both the checked look and the focus ring.
    <label className={`station-timing__chip${chipModifier}`}>
      <input
        type="radio"
        name={radioGroupName}
        value={option.key}
        checked={isSelected}
        onChange={onOptionChange}
        aria-label={t("stationTiming.OPTION_ARIA", {
          option: optionLabel,
          leg: legName,
          time: timeLabel,
        })}
      />
      <span className="station-timing__chip-body">
        <span className="station-timing__chip-value station-timing__numeric">
          {timeLabel}
        </span>
        <span className="station-timing__chip-label">
          <span>{optionLabel}</span>
          {hoursRange ? (
            <span className="station-timing__hours">{hoursRange}</span>
          ) : null}
        </span>
      </span>
    </label>
  );
};
